import { DateTime } from "luxon"
import { getNamedPartecipants } from "./func.utils"
import { regularPartecipants } from "./substitutes.utils"

// ── L'EVENTO DI UNA LEZIONE ────────────────────────────────────────
// L'evento su Google Calendar si ricava dalla lezione salvata: titolo,
// note, orari e luogo. Lo fanno il salvataggio della lezione e il
// controllo che rimette a posto il calendario, con le stesse funzioni: se
// le due strade calcolassero l'evento in modo diverso, il controllo
// "correggerebbe" ogni giorno eventi già giusti.

// Google lascia scegliere l'ID di un evento nuovo, purché fatto di cifre e
// lettere dalla a alla v (base32hex), tra 5 e 1024 caratteri. Ricavarlo
// dalla lezione vuol dire che ogni lezione sa qual è il suo evento anche
// prima che Google risponda: due dispositivi che lo creano insieme, o un
// secondo tentativo dopo una risposta persa, trovano quello che c'è già
// (Google risponde 409) invece di farne un doppione. Ogni carattere dell'ID
// della lezione diventa quattro cifre esadecimali, che stanno tutte in
// base32hex.
export const calendarEventID = lessonID =>
	`incampo${Array.from(String(lessonID), char =>
		char.charCodeAt(0).toString(16).padStart(4, "0"),
	).join("")}`

// Nel titolo vanno gli allievi della lezione, non i sostituti di un giorno,
// e solo quelli con un nome: i posti ancora vuoti farebbero "SAT con , , ".
export const buildEventTitle = (lessonTypeName, partecipants) => {
	const names = getNamedPartecipants(regularPartecipants(partecipants)).map(
		player => player.playerName,
	)
	return names.length > 0 ? `${lessonTypeName} con ${names.join(", ")}` : lessonTypeName
}

// ── IL LINK ALLA LEZIONE ───────────────────────────────────────────
// Sotto le note, l'evento porta il link alla lezione in inCampo (#166):
// dal calendario, a lezione finita, si apre la lezione per scrivere com'è
// andata. La descrizione di un evento Google è testo, e i link li fa lui:
// una riga e l'indirizzo.
//
// L'indirizzo è quello dell'app dei dati della lezione, non quello da cui
// la si sta usando: chi apre l'app in locale sui dati veri, o da una
// preview, riscriverebbe tutti gli eventi col suo indirizzo, e il controllo
// di ogni giorno li riscriverebbe di nuovo. Lo stesso fa il server per gli
// assistenti (`lessonEventDescription` in functions/index.js). Fuori dai
// due progetti — gli emulatori — l'app che si sta usando.
const APP_URLS = {
	hdtennisteammanager: "https://incampo.app",
	"hdtennisteammanager-dev": "https://dev.incampo.app",
}

export const appURL = (projectID = process.env.REACT_APP_FIREBASE_PROJECT_ID) =>
	APP_URLS[projectID] ?? window.location.origin

// Sul progetto dev Google Calendar non c'è (docs/app-dev.md): i maestri dei
// dati di prova possono averlo collegato, ma ogni evento fallirebbe, e ogni
// lezione salvata direbbe "non sincronizzata su Google Calendar". Lì l'app
// il calendario non lo tocca: niente chiamate, niente avvisi, niente coda.
export const calendarAvailable = (projectID = process.env.REACT_APP_FIREBASE_PROJECT_ID) =>
	projectID !== "hdtennisteammanager-dev"

export const lessonLink = lessonID => `${appURL()}/lessons/${encodeURIComponent(lessonID)}`

export const LESSON_LINK_LABEL = "Apri la lezione in inCampo:"

export const buildEventDescription = lesson => {
	const notes = (lesson.lessonNotes ?? "").trim()
	if (!lesson.lessonID) return notes
	const link = `${LESSON_LINK_LABEL}\n${lessonLink(lesson.lessonID)}`
	return notes ? `${notes}\n\n${link}` : link
}

// Quello che deve dire l'evento di una lezione, nella forma che si confronta
const toMillis = value => (typeof value?.toMillis === "function" ? value.toMillis() : null)

export const expectedLessonEvent = (lesson, { eventTitle, eventLocation }) => ({
	summary: eventTitle,
	description: buildEventDescription(lesson),
	location: eventLocation ?? "",
	start: toMillis(lesson.lessonStart),
	end: toMillis(lesson.lessonEnd),
})

// Google restituisce i testi come li ha ricevuti, ma gli a capo possono
// tornare in un'altra forma: senza questo, una nota su due righe
// risulterebbe da correggere ogni giorno.
const sameText = (a, b) =>
	(a ?? "").replace(/\r\n/g, "\n").trim() === (b ?? "").replace(/\r\n/g, "\n").trim()

// Gli orari si confrontano come istanti: Google li scrive col fuso del
// calendario ("17:00+02:00"), la lezione li tiene in UTC.
const sameInstant = (dateTime, millis) => millis !== null && Date.parse(dateTime) === millis

export const eventMatchesLesson = (event, expected) =>
	Boolean(event) &&
	event.status !== "cancelled" &&
	sameText(event.summary, expected.summary) &&
	sameText(event.description, expected.description) &&
	sameText(event.location, expected.location) &&
	sameInstant(event.start?.dateTime, expected.start) &&
	sameInstant(event.end?.dateTime, expected.end)

// ── IL CONTROLLO DI ROUTINE ────────────────────────────────────────
// Il mese in corso e il prossimo: le lezioni che contano, quelle che si
// guardano sul calendario. Un mese diverso si controlla a mano dal profilo.
export const calendarCheckRange = (now = DateTime.now()) => {
	const start = now.startOf("month")
	return { start: start.toJSDate(), end: start.plus({ months: 2 }).toJSDate() }
}

export const monthRange = month => {
	const start = month.startOf("month")
	return { start: start.toJSDate(), end: start.plus({ months: 1 }).toJSDate() }
}

// Una volta al giorno: il giorno dell'ultimo controllo riuscito.
export const calendarCheckDay = (now = DateTime.now()) => now.toISODate()

// ── LA CODA DEL CALENDARIO ─────────────────────────────────────────
// Le operazioni su Google che non sono andate — di solito per la rete — si
// segnano qui e si rifanno dopo. Di una lezione si segna solo quale, non
// cosa le è successo: quando si rifà, si legge com'è diventata e il suo
// evento si mette come lei. Di un evento da togliere invece serve tutto,
// perché la lezione non c'è più.
//
//   { type: "lesson", lessonID }
//   { type: "delete", coachID, eventID }
//
// La stessa lezione o lo stesso evento in coda una volta sola: rimetterli
// in coda prende il posto di prima, con un ID nuovo e i tentativi da capo.
// L'ID nuovo serve a chi sta già rifacendo quello vecchio: togliendo il suo
// non toglie anche quello arrivato nel frattempo.
const sameCalendarTarget = (a, b) =>
	a.type === b.type &&
	(a.type === "delete"
		? a.eventID === b.eventID && a.coachID === b.coachID
		: a.lessonID === b.lessonID)

export const withCalendarRepair = (queue, entry) => {
	const queued = { ...entry, attempts: 0 }
	return queue.some(other => sameCalendarTarget(other, entry))
		? queue.map(other => (sameCalendarTarget(other, entry) ? queued : other))
		: [...queue, queued]
}
